/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React from "react";
import { Dimensions, ScrollView, TouchableOpacity, View } from "react-native";
import { Button, FadeInOutView, Text, styles } from "UpduoLibrary";
import {
  skaleBlack,
  skalePrimaryBlue,
  skaleGreenText,
  skaleSoftRed,
  skaleWebBackgroundGray,
  skaleWhite,
  skaleCircleLightBlue,
  upduoPrimaryBlue,
  colorWithOpacity,
} from "config/UpduoColors";
import * as UpduoAnalytics from "config/UpduoAnalytics";
import KudosModal from "./KudosModal";

interface StepCardProps {
  heading: string;
  recommendedMinutes: number;
  totalSteps: number;
  currentStepIndex: number;
  isKudosCard: boolean;
  showDetailButton: boolean;
  partnerIsReadyToProceed: boolean;
  partnerName: string;
  submitSendKudos: boolean;
  doesEndVideoCall: boolean;
  onSubmitSendKudosButtonPress: () => void;
  onKudosSkipPress: () => void;
  onReadyButtonPress: () => void;
  onDetailPress: () => void;
  kudosSelectedValue: object;
  onKudosSelectedValueChange: (element: any) => void;
  onKudosMessageChange: (message: string) => void;
  selfIsReadyToProceed: boolean;
  buttonsDisabled: boolean;
  delayButton: boolean;
  overTime: number;
}

export default function StepCard({
  heading = "HEADING",
  recommendedMinutes = 1,
  totalSteps = 4,
  currentStepIndex = 0,
  isKudosCard = false,
  showDetailButton = false,
  partnerIsReadyToProceed = false,
  partnerName = "Your partner",
  submitSendKudos,
  doesEndVideoCall,
  onSubmitSendKudosButtonPress,
  onKudosSkipPress,
  onReadyButtonPress,
  onDetailPress,
  kudosSelectedValue,
  onKudosSelectedValueChange,
  onKudosMessageChange,
  selfIsReadyToProceed,
  buttonsDisabled,
  overTime,
}: StepCardProps) {
  const _handleButtonPress = () => {
    let type = "nextStep";
    if (submitSendKudos) {
      type = "submitSendKudos";
      onSubmitSendKudosButtonPress();
    } else {
      onReadyButtonPress();
    }
    UpduoAnalytics.logEvent("user-step-proceed", { type });
  };

  const detailButton = showDetailButton ? (
    <TouchableOpacity
      style={[styles.floatingButton, { backgroundColor: skaleCircleLightBlue }]}
      onPress={() => {
        onDetailPress();
      }}
    >
      <Text
        style={{
          color: skaleWhite,
          fontWeight: "bold",
          fontStyle: "italic",
          fontSize: 12,
        }}
      >
        {"MORE"}
      </Text>
    </TouchableOpacity>
  ) : null;
  const recommendedTag = (
    <Text style={{ fontSize: 14, color: skaleBlack, fontWeight: "bold" }}>
      {`about ${recommendedMinutes} min${recommendedMinutes > 1 ? "s" : ""}`}
    </Text>
  );

  let stepButtonText = "Next Card";
  if (doesEndVideoCall) {
    stepButtonText = "End Conversation";
  } else if (!doesEndVideoCall && !selfIsReadyToProceed) {
    stepButtonText = "Next Card";
  } else if (
    !doesEndVideoCall &&
    selfIsReadyToProceed &&
    !partnerIsReadyToProceed
  ) {
    stepButtonText = "Waiting for Partner...";
  }
  const buttonText = isKudosCard ? "Submit" : stepButtonText;
  const defaultButtonColor = selfIsReadyToProceed
    ? colorWithOpacity(skalePrimaryBlue, 0.3)
    : skalePrimaryBlue;
  const buttonColor = doesEndVideoCall ? skaleSoftRed : defaultButtonColor;
  let readyText = " ";
  if (!isKudosCard && partnerIsReadyToProceed) {
    readyText = `${partnerName} is ready for the next step!`;
  }

  return (
    <View
      style={{
        height: "100%",
        borderRadius: 12,
        backgroundColor: skaleWhite,
      }}
    >
      {isKudosCard ? (
        <KudosModal
          partnerFirstName={partnerName}
          kudosSelectedValue={kudosSelectedValue}
          onKudosSelectedValueChange={onKudosSelectedValueChange}
          onKudosMessageChange={onKudosMessageChange}
        />
      ) : (
        <ScrollView
          style={{
            height: (Dimensions.get("window").height - 150) / 4,
            paddingHorizontal: 25,
            paddingVertical: 10,
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "flex-end",
              justifyContent: "space-between",
              paddingVertical: 10,
            }}
          >
            <Text isHeading={true} style={{ fontSize: 20 }}>
              {`Card ${currentStepIndex + 1}/${totalSteps}`}
            </Text>
            {recommendedTag}
          </View>
          <Text
            style={{
              fontSize: 16,
            }}
          >
            {heading}
          </Text>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              marginVertical: 15,
            }}
          >
            {detailButton}
          </View>
        </ScrollView>
      )}
      <View
        style={{
          borderTopWidth: 1,
          height: 1,
          borderTopColor: skaleWebBackgroundGray,
          alignSelf: "stretch",
          width: "auto",
        }}
      />
      <View
        style={{
          alignItems: "center",
          paddingVertical: 15,
          height: 100,
        }}
      >
        <Button
          disabled={buttonsDisabled}
          text={buttonText}
          onPress={_handleButtonPress}
          color={buttonColor}
          marginHorizontal={30}
          isFlashing={currentStepIndex < totalSteps && partnerIsReadyToProceed}
        />
        {isKudosCard ? (
          <TouchableOpacity
            style={{
              alignItems: "center",
            }}
            onPress={onKudosSkipPress}
          >
            <Text
              isHeading={true}
              style={{
                color: upduoPrimaryBlue,
                marginTop: 10,
              }}
            >
              {"skip"}
            </Text>
          </TouchableOpacity>
        ) : null}
        <Text
          style={{
            marginTop: 2,
            fontSize: 16,
            color: skaleGreenText,
            textAlign: "center",
          }}
        >
          {readyText}
        </Text>
      </View>
      {/* Move on reminder that looks like a tooltip */}
      <FadeInOutView
        visible={overTime > 60 && !selfIsReadyToProceed}
        style={{
          alignItems: "center",
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 90,
        }}
      >
        <View
          style={{
            backgroundColor: colorWithOpacity(skaleBlack, 0.9),
            borderRadius: 4,
            width: 140,
            height: 30,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text style={{ color: skaleWhite }}>{"Click to move on"}</Text>
        </View>
        <View
          style={{
            width: 0,
            height: 0,
            borderLeftWidth: 8,
            borderRightWidth: 8,
            borderTopWidth: 6,
            backgroundColor: "transparent",
            borderLeftColor: "transparent",
            borderRightColor: "transparent",
            borderTopColor: colorWithOpacity(skaleBlack, 0.9),
          }}
        />
      </FadeInOutView>
    </View>
  );
}
